import {apiFetch} from "../client";
import type {RateSource} from "../../types";
import {listQuerySuffix, type ListParams} from "./common";
import type {SecondFactorStatus} from "./secondFactor";

// ─── Merchant domain (admin) ──────────────────────────────────
// Источник правды по телам: internal/merchant/handler.go + oapi-spec.yml.
// Все ручки под RoleAdmin (см. internal/router/router.go блок /v1/merchant).

/** GET /v1/merchant/{id}, ответ POST /v1/merchant/search, PATCH /v1/merchant/{id}. */
export interface MerchantResponse {
    id: string;
    name: string;
    balanceId: string;
    currency: string;
    amount: string;
    currencyScale: number;
    maxPaymentsPerOrder: number;
    claimHoldMinutes: number;
    isVip: boolean;
    // Овердрафт («драфт»): работа в долг. overdraftLimit — человеческая
    // USDT-строка с бэка (или null, если не задан); НЕ парсить в float для
    // отображения. В предоплате это допуск овердрафта (питает claim-гейт),
    // в долговом режиме — сигнальный лимит.
    overdraftEnabled: boolean;
    overdraftLimit: string | null;
    // Живой размер превышения долга над лимитом (готовая USDT-строка с бэка,
    // тот же scale, что и у лимита) или null, когда долг в пределах / неприменимо.
    // Считается бэкендом (alert.ExcessAtomic) — клиент деньги не пересчитывает.
    debtExceededBy: string | null;
    // Источник курса: "manual" — курс вводит трейдер при завершении платежа;
    // "rapira" — курс заявки фиксируется из курса биржи при её создании.
    // rateMarkupPercent — ставка со знаком (строка), задана ровно при "rapira".
    rateSource: RateSource;
    rateMarkupPercent: string | null;
    exchangeSellMarkupPercent?: string | null;
    inRateSource?: RateSource;
    inRateMarkupPercent?: string | null;
    outRateSource?: RateSource;
    outRateMarkupPercent?: string | null;
    // Расчёт по мерчанту гарантирован внешним источником: замок предоплаты к его
    // заявкам не применяется. Только чтение — поддерживается привязкой к кабинету
    // и снимается отвязкой, отдельно не редактируется.
    settlementGuaranteed: boolean;
    // "internal" — баланс считаем мы; "external" — баланс ЧИТАЕТСЯ из книги
    // кабинета площадки. У внешнего не рисуем ни овердрафтную лампу, ни
    // предоплатную: обе построены вокруг предоплаты, которой здесь нет, а сам
    // баланс сменил и знак, и смысл.
    // "external_debt" — кабинет работает в долг: минус пишет наш расчёт по нашей
    // заявке, гасят поступления на привязанный кошелёк. Баланс снова означает
    // долг, но порогов у него нет — прегейт предоплаты к кабинету не применяется.
    settlementSource: string;
    // Кабинет площадки; null у обычного мерчанта, пришедшего по API.
    external: ExternalMerchantLink | null;
}

/** Мерчант, чей баланс читается из книги кабинета, а не считается нами. */
export function isMirroredMerchant(merchant: MerchantResponse): boolean {
    return merchant.settlementSource === "external";
}

/**
 * Мерчант кабинета в долговом режиме. Не зеркало: баланс считаем мы, книга
 * кабинета платит только команде. Отдельный предикат, а не ветка внутри
 * isMirroredMerchant, — иначе от него отвалились бы блок книги и вся логика,
 * которая у зеркала есть, а у долгового кабинета её нет.
 */
export function isDebtCabinetMerchant(merchant: MerchantResponse): boolean {
    return merchant.settlementSource === "external_debt";
}

/** Внешний блок карточки мерчанта (handler.ExternalMerchantResponse). */
export interface ExternalMerchantLink {
    source: string;
    /** Код кабинета; тот же, что мы пишем в EXTSOURCE_PAYLORD_ACCOUNTS. */
    accountCode: string;
    /** Переключатель «принимать заявки». Выключен — заявки кабинета не создаются. */
    ordersEnabled: boolean;
    autoProvisioned: boolean;
}

/**
 * GET /v1/merchant/{id}/book — книга кабинета за мерчантом-зеркалом
 * (handler.BookMirrorResponse).
 *
 * Все суммы — готовые строки с бэкенда. Не парсить в число: деньги здесь
 * четырёх- и шестизначные после точки, и float их не хранит.
 */
export interface MerchantBookResponse {
    source: string;
    accountCode: string;
    currency: string;
    /** Четыре корзины их страницы. Совпадать надо именно с разбивкой. */
    baskets: {
        main: string;
        cashback: string;
        frozen: string;
        /** null — их строка страховой депозит не отдаёт. В баланс он не входит. */
        insurance: string | null;
    };
    /** Main + Cashback — единственная комбинация, которая и есть баланс. */
    balance: string;
    /**
     * Прибыль ДВУМЯ числами: «начислено» и «из них перенесено на рабочий
     * баланс». Ни одно не равно остатку корзины кэшбэка и не обязано.
     */
    profit: {
        accrued: string;
        movedToWorking: string;
    };
    /** Возраст данных: сколько строк храним, какая последняя, когда прочитали. */
    rowsStored: number;
    tailExternalId: number | null;
    tailAt: string | null;
    observedAt: string | null;
    /** Сверка. Норма — diff ровно "0" и matches true; порога здесь нет. */
    ours: string;
    theirs: string;
    diff: string;
    matches: boolean;
}

/** PUT /v1/merchant/{id}/external-link. */
export interface LinkExternalMerchantRequest {
    source: string;
    accountCode: string;
}

export interface ListMerchantsResponse {
    items: MerchantResponse[];
    limit: number;
    offset: number;
    hasMore: boolean;
    nextOffset?: number | null;
}

/**
 * PATCH /v1/merchant/{id} body. Все поля опциональны
 * (отсутствие = не менять). Границы берём из бэкенда:
 * maxPaymentsPerOrder 1..10 (maxPaymentsPerOrderLimit),
 * claimHoldMinutes is an integer >= 1.
 * overdraftLimit — USDT-строка (≥0, ≤6 знаков);
 * пустая строка "" = сбросить поле в null.
 *
 * inRateSource/inRateMarkupPercent и outRateSource/outRateMarkupPercent —
 * независимые пары. Передаём только изменяемое направление; manual требует
 * пустой процент. Исторический знак сохраняется при сохранении без изменений.
 * Legacy rateSource и rateMarkupPercent — ОДНА настройка из двух полей: бэк принимает
 * их только парой (одно без другого → 400). Оба отсутствуют = не менять.
 * "manual" требует rateMarkupPercent: "", "rapira" — строку со знаком
 * (до 2 знаков, > −100). Строка, а не число: JSON-число ушло бы через float64.
 */
export interface UpdateMerchantRequest {
    maxPaymentsPerOrder?: number;
    claimHoldMinutes?: number;
    isVip?: boolean;
    overdraftEnabled?: boolean;
    overdraftLimit?: string;
    rateSource?: RateSource;
    rateMarkupPercent?: string;
    exchangeSellMarkupPercent?: string;
    inRateSource?: RateSource;
    inRateMarkupPercent?: string;
    outRateSource?: RateSource;
    outRateMarkupPercent?: string;
}

/** Ответ POST/DELETE /v1/merchant/{id}/api-token (handler.MerchantAPITokenResponse). */
export interface MerchantAPITokenResponse {
    merchantId: string;
    /** Сырой токен "pm_…" — присутствует ТОЛЬКО в ответе rotate (показать один раз). */
    token?: string;
    createdAt?: string;
    /** true в ответе revoke. */
    revoked?: boolean;
}

export interface MerchantPortalAccessRequest {
    login: string;
    password: string;
}

export interface MerchantPortalAccessResponse {
    enabled: boolean;
    login?: string;
    updatedAt?: string;
    /** Пользователь кабинета; нет, пока доступ ни разу не выдавали. */
    userId?: string;
    secondFactor?: SecondFactorStatus;
}

export type MerchantCapabilityName =
    | "exchange.workflow"
    | "order.client-paid"
    | "receipt.client-upload"
    | "order.cancel"
    | "status.extended";

export interface MerchantCapability {
    name: MerchantCapabilityName;
    enabled: boolean;
}

export interface MerchantCapabilitiesResponse {
    items: MerchantCapability[];
}

/**
 * POST /v1/merchant/search — точное совпадение по имени, возвращает ОДНОГО
 * мерчанта (sqlc `FindByName :one`, `WHERE name = $1`) либо 404. Не список.
 */
export function searchMerchantByName(token: string | null, name: string): Promise<MerchantResponse> {
    return apiFetch<MerchantResponse>("/merchant/search", token, {
        method: "POST",
        body: JSON.stringify({name}),
    });
}

/**
 * GET /v1/merchant — admin-only список неархивных мерчантов.
 *
 * Разреза по клиенту площадки здесь нет намеренно: кабинет принимает заявки
 * любых клиентов площадки, поэтому «через кого из наших прошёл клиент 902» —
 * вопрос про заявки, а не про мерчантов.
 */
export function listMerchants(
    token: string | null,
    query: ListParams = {},
    filter: MerchantListFilter = {},
): Promise<ListMerchantsResponse> {
    return apiFetch<ListMerchantsResponse>(
        `/merchant${listQuerySuffix(query)}${externalFilterSuffix(query, filter)}`,
        token,
    );
}

export interface MerchantListFilter {
    externalSource?: string;
}

function externalFilterSuffix(query: ListParams, filter: MerchantListFilter): string {
    const params = new URLSearchParams();
    if (filter.externalSource) params.set("externalSource", filter.externalSource);
    const suffix = params.toString();
    if (!suffix) return "";
    return listQuerySuffix(query) ? `&${suffix}` : `?${suffix}`;
}

/** PUT /v1/merchant/{id}/external-link — привязка кабинета к существующему мерчанту. */
export function linkMerchantExternalPair(
    token: string | null,
    id: string,
    body: LinkExternalMerchantRequest,
): Promise<MerchantResponse> {
    return apiFetch<MerchantResponse>(`/merchant/${encodeURIComponent(id)}/external-link`, token, {
        method: "PUT",
        body: JSON.stringify(body),
    });
}

/** PATCH /v1/merchant/{id}/external-link — переключатель «принимать заявки». */
export function setMerchantExternalOrders(
    token: string | null,
    id: string,
    ordersEnabled: boolean,
): Promise<MerchantResponse> {
    return apiFetch<MerchantResponse>(`/merchant/${encodeURIComponent(id)}/external-link`, token, {
        method: "PATCH",
        body: JSON.stringify({ordersEnabled}),
    });
}

/** GET /v1/merchant/{id}. */
export function getMerchant(token: string | null, id: string): Promise<MerchantResponse> {
    return apiFetch<MerchantResponse>(`/merchant/${encodeURIComponent(id)}`, token);
}

/** GET /v1/merchant/{id}/capabilities — полный реестр, false по умолчанию. */
export function getMerchantCapabilities(
    token: string | null,
    id: string,
): Promise<MerchantCapabilitiesResponse> {
    return apiFetch<MerchantCapabilitiesResponse>(
        `/merchant/${encodeURIComponent(id)}/capabilities`,
        token,
    );
}

/** PUT /v1/merchant/{id}/capabilities/{capability}. */
export function setMerchantCapability(
    token: string | null,
    id: string,
    capability: MerchantCapabilityName,
    enabled: boolean,
): Promise<MerchantCapability> {
    return apiFetch<MerchantCapability>(
        `/merchant/${encodeURIComponent(id)}/capabilities/${encodeURIComponent(capability)}`,
        token,
        {method: "PUT", body: JSON.stringify({enabled})},
    );
}

/**
 * GET /v1/merchant/{id}/book — книга кабинета мерчанта-зеркала.
 *
 * Отдельная ручка, а не поле карточки: она стоит запроса по сохранённой книге,
 * и список мерчантов не должен платить его на каждую строку. 404 у обычного
 * мерчанта — это ответ, а не сбой.
 */
export function getMerchantBook(
    token: string | null,
    id: string,
): Promise<MerchantBookResponse> {
    return apiFetch<MerchantBookResponse>(`/merchant/${encodeURIComponent(id)}/book`, token);
}

/** GET /v1/merchant/{id}/portal-access — статус единственного browser user. */
export function getMerchantPortalAccess(
    token: string | null,
    id: string,
): Promise<MerchantPortalAccessResponse> {
    return apiFetch<MerchantPortalAccessResponse>(
        `/merchant/${encodeURIComponent(id)}/portal-access`,
        token,
    );
}

/** PUT /v1/merchant/{id}/portal-access — выдача/сброс с отзывом всех старых сессий. */
export function putMerchantPortalAccess(
    token: string | null,
    id: string,
    body: MerchantPortalAccessRequest,
): Promise<MerchantPortalAccessResponse> {
    return apiFetch<MerchantPortalAccessResponse>(
        `/merchant/${encodeURIComponent(id)}/portal-access`,
        token,
        {
            method: "PUT",
            body: JSON.stringify(body),
        },
    );
}

/** DELETE /v1/merchant/{id}/portal-access — отключение browser user и всех сессий. */
export function deleteMerchantPortalAccess(
    token: string | null,
    id: string,
): Promise<MerchantPortalAccessResponse> {
    return apiFetch<MerchantPortalAccessResponse>(
        `/merchant/${encodeURIComponent(id)}/portal-access`,
        token,
        {method: "DELETE"},
    );
}

/** PATCH /v1/merchant/{id} — только переданные поля. */
export function updateMerchant(
    token: string | null,
    id: string,
    body: UpdateMerchantRequest
): Promise<MerchantResponse> {
    return apiFetch<MerchantResponse>(`/merchant/${encodeURIComponent(id)}`, token, {
        method: "PATCH",
        body: JSON.stringify(body),
    });
}

/** POST /v1/merchant/{id}/api-token — ротация: сырой токен приходит один раз. */
export function rotateMerchantApiToken(
    token: string | null,
    id: string
): Promise<MerchantAPITokenResponse> {
    return apiFetch<MerchantAPITokenResponse>(`/merchant/${encodeURIComponent(id)}/api-token`, token, {
        method: "POST",
    });
}

/** DELETE /v1/merchant/{id}/api-token — отзыв активного токена. */
export function revokeMerchantApiToken(
    token: string | null,
    id: string
): Promise<MerchantAPITokenResponse> {
    return apiFetch<MerchantAPITokenResponse>(`/merchant/${encodeURIComponent(id)}/api-token`, token, {
        method: "DELETE",
    });
}

/** DELETE /v1/merchant/{id} — soft-delete: archived_at + revoke active API tokens. */
export function archiveMerchant(token: string | null, id: string): Promise<MerchantResponse> {
    return apiFetch<MerchantResponse>(`/merchant/${encodeURIComponent(id)}`, token, {
        method: "DELETE",
    });
}
